import {
  Button,
  Table,
  TableContainer,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  HStack,
  Spinner,
  Select as ChakraSelect,
  Heading,
  Box,
  Divider,
  useColorModeValue,
  Flex,
  Icon
} from "@chakra-ui/react";
import React, { useContext, useState, useEffect } from "react";
import { RoughNotation } from "react-rough-notation";
import { Helmet } from "react-helmet";
import Select from "react-select";
import { AdminGetOtpHistoryAPI } from "../../../utils/api";
import OtpHistoryCard from "../components/OtpHistoryCard";
import { AppContext } from "../../../context/AppContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";

const DurationData = [
  { label: "All time", value: "all" },
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 days", value: "lastWeek" },
  { label: "Last 30 days", value: "lastMonth" },
];

const StatusData = [
  { label: "All", value: "all" },
  { label: "Success", value: "success" },
  { label: "Pending", value: "pending" },
  { label: "Cancelled", value: "cancelled" },
];

const OtpHistory = () => {
  const { AdminToken } = useContext(AppContext);
  const [pageNo, setPageNo] = useState(1);
  const [limitNo, setLimitNo] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [durationFilter, setDurationFilter] = useState("today");
  const [statusFilter, setStatusFilter] = useState("all");
  const [data, setData] = useState([]);
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("userId");
  const [loading, setLoading] = useState(false);
  const [totalOtpPrice, setTotalOtpPrice] = useState(0);
  const navigate = useNavigate();

  // Theme Colors
  // Theme Colors
  const bg = "gray.900";
  const textColor = "white";
  const subTextColor = "gray.400";
  const borderColor = "gray.700";
  const headerBg = "gray.800";
  const themeColor = "#319795";

  const selectBg = "#2D3748";
  const selectBorder = "#4A5568";
  const selectBorderHover = "#718096";
  const optionBgFocused = "teal.900";
  const optionColor = "white";

  useEffect(() => {
    setLoading(false);
    AdminGetOtpHistoryAPI(AdminToken, pageNo, limitNo, durationFilter, statusFilter, userId)
      .then((res) => {
        setLoading(true);
        setData(res.data.data);
        setTotalPages(res.data.total_pages);
        setTotalOtpPrice(res.data.totalMobileOtpPrice);
      })
      .catch((err) => {
        setLoading(true);
        console.error("Error fetching OTP history:", err);
      });
  }, [limitNo, pageNo, durationFilter, statusFilter, userId]);

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: selectBg,
      borderColor: state.isFocused ? themeColor : selectBorder,
      boxShadow: state.isFocused ? `0 0 0 1px ${themeColor}` : "none",
      "&:hover": {
        borderColor: state.isFocused ? themeColor : selectBorderHover,
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: selectBg,
      zIndex: 10,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? themeColor
        : state.isFocused
          ? optionBgFocused
          : "transparent",
      color: state.isSelected ? "white" : optionColor,
      "&:active": {
        backgroundColor: themeColor,
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: optionColor,
    }),
  };

  return (
    <>
      <Helmet>
        <title>OTP History | Techy India</title>
      </Helmet>

      <VStack
        align="start"
        bg={bg}
        p={["1rem", "1.5rem", "2rem"]}
        w={"100%"}
        minH="100vh"
        mx="auto"
        gap="1.5rem"
      >
        {/* Page Header */}
        <VStack align="start" w="100%">
          <Heading size="lg" display={'flex'} alignItems={'center'} color={textColor}>
            {userId && <Icon as={FaArrowLeft} cursor={'pointer'} w={'1.5rem'} h={'1.5rem'} mr={'1rem'} onClick={() => { navigate('/admin/otp-history', { replace: true }) }} color={textColor} />}
            OTP{" "}
            <RoughNotation
              strokeWidth={2}
              color={themeColor}
              type="underline"
              show={true}
            >
              History
            </RoughNotation>
          </Heading>
        </VStack>

        {/* Filter Bar */}
        <Box
          w="100%"
          bg={headerBg}
          p="1rem"
          borderRadius="md"
          boxShadow="sm"
          display="flex"
          flexWrap="wrap"
          justifyContent="space-between"
          alignItems="center"
          gap="0.5rem"
          borderWidth="1px"
          borderColor={borderColor}
        >
          <HStack w={["100%", "100%", "30%", "30%"]}>
            <Box w="100%">
              <Select
                onChange={(e) => {
                  setDurationFilter(e.value);
                  setPageNo(1);
                }}
                defaultValue={DurationData[1]}
                options={DurationData}
                styles={customSelectStyles}
              />
            </Box>
          </HStack>

          <HStack w={["100%", "100%", "45%", "35%"]}>
            <Box w="100%">
              <Select
                onChange={(e) => {
                  setStatusFilter(e.value);
                  setPageNo(1);
                }}
                defaultValue={StatusData[0]}
                options={StatusData}
                styles={customSelectStyles}
              />
            </Box>
          </HStack>

          {userId && <HStack fontSize={'1rem'} fontWeight={700}>
            <Text color={textColor}>Total</Text> <Text as={'span'} color={'green.500'}>₹{totalOtpPrice}</Text>
          </HStack>
          }
          <HStack>
            <Text fontSize="sm" color={subTextColor}>
              Items per page:
            </Text>
            <ChakraSelect
              w="80px"
              onChange={(e) => {
                setLimitNo(Number(e.target.value));
                setPageNo(1);
              }}
              value={limitNo}
              borderColor={borderColor}
              _focus={{ borderColor: themeColor }}
            >
              {[10, 20, 40, 60, 80, 100].map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </ChakraSelect>
          </HStack>
        </Box>

        <Divider borderColor={borderColor} />

        {/* Loading State */}
        {!loading && (
          <VStack justify="center" w="100%" minH="50vh">
            <Spinner
              thickness="5px"
              speed="0.55s"
              emptyColor="gray.200"
              color={themeColor}
              size="xl"
            />
            <Text color={subTextColor}>Loading OTP history...</Text>
          </VStack>
        )}

        {/* Data Table */}
        {data.length && loading ? (
          <TableContainer
            w="100%"
            boxShadow="sm"
            borderRadius="md"
            overflowX="auto"
            bg={bg}
            borderWidth="1px"
            borderColor={borderColor}
          >
            <Table variant="simple" size={["sm", "md"]}>
              <Thead bg={headerBg}>
                <Tr>
                  {[
                    "Email",
                    "Service",
                    "Price",
                    "Mobile",
                    "Status",
                    "OTP",
                    "Date",
                  ].map((head) => (
                    <Th
                      key={head}
                      color={subTextColor}
                      textAlign="center"
                      p="0.8rem"
                      fontSize="sm"
                      borderColor={borderColor}
                    >
                      {head}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {data.map((el, index) => (
                  <OtpHistoryCard
                    key={el._id}
                    otp={el}
                    index={index}
                    themeColor={themeColor}
                    borderColor={borderColor}
                    textColor={textColor}
                    subTextColor={subTextColor}
                  />
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        ) : (
          <></>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <HStack
            gap="0.8rem"
            align="center"
            justify="center"
            w="100%"
            mt="1.5rem"
            wrap="wrap"
          >
            <Button
              color={themeColor}
              borderColor={themeColor}
              variant="outline"
              onClick={() => setPageNo((p) => Math.max(1, p - 1))}
              isDisabled={pageNo === 1}
              _hover={{ bg: "teal.50" }}
            >
              {"<<"}
            </Button>
            <Text
              fontWeight="bold"
              fontSize="md"
              border="1px solid"
              borderColor={borderColor}
              px="1rem"
              py="0.2rem"
              borderRadius="md"
              color={textColor}
            >
              {pageNo}
            </Text>
            <Text fontWeight="bold" color={subTextColor}>
              / {totalPages}
            </Text>
            <Button
              color={themeColor}
              borderColor={themeColor}
              variant="outline"
              onClick={() =>
                setPageNo((p) => (p < totalPages ? p + 1 : p))
              }
              isDisabled={pageNo >= totalPages}
              _hover={{ bg: "teal.50" }}
            >
              {">>"}
            </Button>
          </HStack>
        )}
      </VStack>
    </>
  );
};

export default OtpHistory;
